import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { select, Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import {
  AppParametersService,
  AppState,
  BaseAppParameters,
  FilterState,
  RealEstateObject,
  RealEstateResponse,
} from '@immosky/types';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  map,
  switchMap,
  tap,
} from 'rxjs/operators';
import {
  Actions,
  createEffect,
  ofType,
  ROOT_EFFECTS_INIT,
} from '@ngrx/effects';
import { selectFilterState } from '@immosky/object-search-filter';
import { convertResponseToRealEstateObjects } from '@immosky/shared';
import { resetMapRealEstateObjects } from '../../state/results/map-results/map-results.actions';
import { setSimilarRealEstateObjects } from '../../state/results/similar-results/similar-results.actions';
import { SimilarObjectSearchQueryBuilder } from './similar-objects-search-query-builder';

@Injectable({
  providedIn: 'root',
})
export class SimilarObjectsSearchEffect {
  private filterState!: FilterState;

  init$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ROOT_EFFECTS_INIT),
      switchMap(() => {
        return this.store.pipe(
          select(selectFilterState),
          debounceTime(100),
          distinctUntilChanged(),
          switchMap((filterState: FilterState) => {
            this.filterState = filterState;

            const queryBuilder = new SimilarObjectSearchQueryBuilder();
            queryBuilder.setFilterState(
              this.filterState,
              this.appParametersService.get().regionsIndex
            );
            const query = queryBuilder.build();

            return this.search(query).pipe(
              tap(() => {
                this.store.dispatch(resetMapRealEstateObjects());
              }),
              switchMap((results: RealEstateObject[]) =>
                of(
                  setSimilarRealEstateObjects({
                    objects: results,
                  })
                )
              )
            );
          })
        );
      })
    )
  );

  constructor(
    private httpClient: HttpClient,
    private store: Store<AppState>,
    private actions$: Actions,
    private appParametersService: AppParametersService<BaseAppParameters>
  ) {}

  search(query: unknown): Observable<RealEstateObject[]> {
    return this.httpClient
      .post<RealEstateResponse>(
        `${this.appParametersService.get().apiBaseUrl}/${
          this.appParametersService.get().propertiesIndex
        }/_search`,
        query
      )
      .pipe(
        map((response: RealEstateResponse) => {
          return convertResponseToRealEstateObjects(response);
        }),
        catchError((error) => {
          console.error(error);
          return of([]);
        })
      );
  }
}
